/* lp-shelf.js — the collection "shelf": one product family, its colour swatches, size/insert chips,
   the photograph stage with view arrows, and add-to-bag. Ported 16 Sep 2026 from the prototype's
   buildCfg/renderCfg/pickCfg/stepView/playFilm/addBag/atcPop/atcConfetti and the swatch hover-to-select
   (website/Leatherpress_Website_v4_branch2.html). Behaviour is the prototype's:
     - a colour chosen from a swatch WIPES the new photograph out of that swatch (Web Animations,
       fill:forwards, committed before the effect is lifted — see cancelWipe), 1750ms from --wipe;
     - the colour NAME lands halfway through (CNAMEAT = 0.5), everything else on the commit;
     - the live colour (--live on <main>, the bar under the hero) and the buy buttons' tint follow;
     - the Inspire film keeps playing UNDER the opening photograph — no white flash (FILMHANDOFF);
     - hovering a swatch for 250ms selects it; the film chip is click-only;
     - a superseded wipe is frozen where it was and wiped over — nothing snaps.
   What changed for Shopify: the family and its colourways arrive as JSON from the section (family
   record + each product's lpress.* metafields + live variants), sizes/inserts map to real variants,
   "details" is the product URL, and add-to-bag posts to Shopify's cart through Horizon's own event
   so its drawer opens and its icon updates. Reduced-motion visitors get instant states. */
(function () {
  'use strict';
  if (!('customElements' in window) || customElements.get('lp-shelf')) return;

  var CNAMEAT = 0.5;            /* how early the colour name changes, as a fraction of the wipe */
  var FILMHANDOFF = true;       /* false = the original hard swap out of the film */
  var HOVER_MS = 250;

  function noMotion() { try { return !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; } }
  /* Phones (820px and under) never see the stage: the card grid replaces it (lp-collection-shelf.liquid),
     yet its 1600px photographs, and warm()'s preload of every colourway's front, were fetched anyway, about
     2 MB on the Inspire page (23 Sep 2026). A lazy image inside a hidden column is not fetched; one on screen
     loads at once, so desktop is unchanged and a phone turned sideways past 820px still gets its photo. */
  function phoneLayout() { try { return !!(window.matchMedia && matchMedia('(max-width:820px)').matches); } catch (e) { return false; } }
  function wipeMs() {
    try { var v = getComputedStyle(document.documentElement).getPropertyValue('--wipe').trim(); var n = parseFloat(v); if (!n) return 1750; return (v.indexOf('ms') > -1 ? n : n * 1000) + 60; }
    catch (e) { return 1810; }
  }
  function wipeDur() { return Math.max(0, wipeMs() - 60); }
  /* 30 Sep 2026 — the photographs, sized to the stage and fetched ahead. Luke: "the images are taking too long
     to load. It takes nearly two seconds to scroll between images and I think its because the image is too big
     ... This only occurs when you switch to a color that wasn't there on load and then scroll through them."
     Measured on the live site that day (1440x900, 10 Mbps, 40ms, empty cache): every frame came 1600px wide
     (50-550 KB, WebP or JPEG as Shopify's CDN chose) into a stage 765px wide; only Inspire fetched anything
     ahead, and only each colour's FIRST frame; so every arrow click waited for a full download, 0.3-2.0s.
     The fade ran out while it waited, and the browser keeps showing an image's old file until the new one
     has arrived, so the shopper saw the old photograph sit still, then the new one cut in with no fade.
     Now:
       - the stage asks the CDN for the smallest of STEPS that covers its own width x the screen's pixel
         density (a 1x screen at 1440 gets 800px, about a sixth of the bytes; a retina screen still gets 1600);
       - the magnifier and the photo viewer keep the section's full 1600px frame: the mouse resting on the
         photograph for SHARP_MS (the magnifier itself waits 650ms) fetches it and swaps it in once decoded,
         the same picture, so nothing moves (sharpen); a mouse only passing over fetches nothing extra;
       - once a colour is chosen or an arrow pressed, that colour's other frames are fetched one at a time,
         nearest first (next, previous, then onward), after the frame on screen is ready (ahead/pump);
       - the animations are exactly as before: the colour wipe and the .34s arrow fade (a hold-then-faster-fade
         was tried and reverted the same day at Luke's request). HOLD_MS caps the waits for a frame to decode. */
  var STEPS = [800, 1000, 1200, 1400, 1600];
  var HOLD_MS = 2500, SHARP_MS = 80;
  /* the frames fetched ahead are held (their Image objects, the file as downloaded, not decoded) until KEEP_N
     newer ones replace them: once nothing holds a fetched image the browser may drop its copy, and without its
     disk cache (a private window, a full cache, the cache switched off) the click then downloaded it again.
     Measured 30 Sep 2026: 3 of 41 photos fetched twice with the cache off. 16 covers the colour on screen plus
     each colour's first frame; about 1 MB on a 1x screen, 5 MB on a retina one */
  var KEEP_N = 16;
  function widthOf(u) { var m = /[?&]width=(\d+)/.exec(u || ''); return m ? parseInt(m[1], 10) : 0; }
  function atWidth(u, w) { return String(u).replace(/([?&]width=)\d+/, '$1' + w); }
  /* the same photograph at another width */
  function samePhoto(a, b) { var s = function (u) { return String(u || '').replace(/[?&]width=\d+/, ''); }; return !!a && s(a) === s(b); }
  /* resolves once the image is decoded, has failed, or cap ms have passed. A browser may refuse decode() when
     the file changes under it (the spec's "current request changes"); then its load event stands in */
  function ready(img, cap) {
    return new Promise(function (res) {
      var done = false, fin = function () { if (!done) { done = true; res(); } };
      var loaded = function () { img.removeEventListener('load', loaded); img.removeEventListener('error', loaded); if (img.decode) img.decode().then(fin, fin); else fin(); };
      var wait = function () { if (img.complete) fin(); else { img.addEventListener('load', loaded); img.addEventListener('error', loaded); } };
      if (img.decode) img.decode().then(fin, wait); else wait();
      setTimeout(fin, cap);
    });
  }
  function shade(hex, mul) {
    var n = parseInt(hex.slice(1), 16);
    var r = Math.round(((n >> 16) & 255) * mul), g = Math.round(((n >> 8) & 255) * mul), b = Math.round((n & 255) * mul);
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }
  /* Butter, Quartz and Reef are light enough that bone-on-colour would not read; decide by luminance */
  function readableOn(hex) {
    var r = parseInt(hex.substr(1, 2), 16) / 255, g = parseInt(hex.substr(3, 2), 16) / 255, b = parseInt(hex.substr(5, 2), 16) / 255;
    function lin(v) { return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }
    var L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
    return L > 0.42 ? '#191510' : '#fdfaf3';
  }
  function money(cents) { var n = cents / 100; return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2)); }
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }
  /* the swatch's centre as a point in the page; each element converts it to its own percentages,
     and the radius is returned in pixels — the exact distance to the furthest corner, plus a little */
  function originIn(el, v) {
    if (!el || !v) return null;
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    if (!isFinite(v.vx) || !isFinite(v.vy)) return null;
    var dx = Math.max(Math.abs(v.vx - r.left), Math.abs(v.vx - (r.left + r.width)));
    var dy = Math.max(Math.abs(v.vy - r.top), Math.abs(v.vy - (r.top + r.height)));
    var out = { x: ((v.vx - r.left) / r.width) * 100, y: ((v.vy - r.top) / r.height) * 100, r: Math.ceil(Math.sqrt(dx * dx + dy * dy)) + 24 };
    if (!isFinite(out.x) || !isFinite(out.y) || !isFinite(out.r)) return null;   /* NaN would silently kill the clip-path */
    return out;
  }

  /* ---- Shopify cart: lp-cart.js makes Horizon's own request and dispatches its own event, so the
     drawer opens and the count updates. The harness replaces it through window.LPCart. ---- */
  function cartAdd(host, variantId, qty, productId) {
    if (window.LPCart && typeof window.LPCart.add === 'function') return Promise.resolve(window.LPCart.add(variantId, qty, productId));
    if (window.lpCartAdd) return window.lpCartAdd(host, variantId, qty, productId);
    return Promise.reject(new Error('lp-cart.js is not loaded'));
  }

  class LPShelf extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      var node = this.querySelector('script[type="application/json"][data-shelf]');
      var data = null; try { data = JSON.parse(node.textContent); } catch (e) { data = null; }
      if (!data || !data.colors || !data.colors.length) return;
      this.fam = data.fam || {}; this.colors = data.colors;
      this.fam.sizes = Array.isArray(this.fam.sizes) && this.fam.sizes.length ? this.fam.sizes : [{ id: 'std', l: '', p: 0 }];
      this.fam.addons = Array.isArray(this.fam.addons) && this.fam.addons.length ? this.fam.addons : null;
      var self = this;
      this.st = { c: 0, s: 0, a: 0, q: 1, v: 0 };
      /* a colourway none of whose variants can be bought is sold out, whatever the flag said; so is one
         with no size or insert choice whose own variant is unavailable (the Lined insert: the product
         stays "available" through the duplicate Blank/Dotted variants it should not carry) */
      this.colors.forEach(function (c) {
        c.pics = (c.pics || []).filter(Boolean); c.variants = c.variants || [];
        if (c.variants.length && !c.variants.some(function (v) { return v.available; })) c.oos = true;
        if (!c.oos && c.variants.length && self.fam.sizes.length === 1 && !self.fam.addons) { var own = self.variantFor(c, self.fam.sizes[0], null); if (own && !own.available) c.oos = true; }
      });
      /* open on something that can be bought: when the first colourway is sold out, start on the first one in
         stock (24 Sep 2026: the Insert shelf opened on the sold-out Lined, so the first thing shown was SOLD OUT) */
      if (this.colors.length && this.colors[0].oos) { var firstIn = this.colors.findIndex(function (c) { return !c.oos; }); if (firstIn > 0) this.st.c = firstIn; }
      this.WIPEFROM = null; this.WIPEJOB = null; this.BTNPREV = null; this.NAMESEQ = 0; this.NAMETIMER = null; this.hoverT = null; this._timers = [];
      this.stage = this.querySelector('[data-stage]');
      var unbind = []; this._unbind = function () { unbind.forEach(function (f) { f(); }); };
      function on(t, ev, fn, opt) { t.addEventListener(ev, fn, opt); unbind.push(function () { t.removeEventListener(ev, fn, opt); }); }
      on(this, 'click', function (e) { self.onClick(e); });
      /* Hover-to-select was the prototype's behaviour (250ms on a swatch picked it). The dev review of
         22 Sep 2026 called it frustrating, and Luke agreed: a swatch now changes only on click. The hover
         highlight stays (CSS). HOVER_SELECT flips it back if wanted. */
      var HOVER_SELECT = false;
      var HOVEROK = HOVER_SELECT && !!(window.matchMedia && matchMedia('(hover:hover)').matches);
      if (HOVEROK) {
        on(this, 'mouseover', function (e) {
          var t = e.target; if (!t || !t.closest) return;
          var chip = t.closest('.cchip'); if (!chip || !self.contains(chip)) return;
          self.clearHover();
          if (chip.classList.contains('filmchip') || chip.classList.contains('on')) return;   /* the film is click-only */
          var ci = parseInt(chip.getAttribute('data-ci'), 10);
          if (ci >= 0) self.hoverT = setTimeout(function () { self.hoverT = null; self.pick('c', ci, chip); }, HOVER_MS);
        });
        on(this, 'mouseout', function (e) { if (e.target && e.target.closest && e.target.closest('.cchip')) self.clearHover(); });
      }
      /* photographs fetched ahead (30 Sep 2026, see STEPS): GOT is every address this shelf has asked for */
      this.GOT = {}; this.PQ = null; this.PQBUSY = false; this.PQT = null; this.RSZ = null; this.AHEADKEY = null; this.SWAPTO = null; this.CHIPT = null; this.SHARPT = null; this.KEPT = [];
      /* a colour the mouse rests on (60ms), or a finger lands on, starts its first frame before the click */
      var early = function (e) {
        var chip = e.target && e.target.closest ? e.target.closest('.cchip[data-ci]') : null;
        clearTimeout(self.CHIPT); if (!chip || !self.contains(chip)) return;
        var col = self.colors[parseInt(chip.getAttribute('data-ci'), 10)]; if (!col) return;
        var go = function () { self.grab(self.sized((col.pics && col.pics[0]) || col.img), true); };
        if (e.type === 'pointerdown') go(); else if (e.pointerType === 'mouse') self.CHIPT = setTimeout(go, 60);
      };
      on(this, 'pointerover', early);
      on(this, 'pointerdown', early);
      on(this, 'pointerout', function () { clearTimeout(self.CHIPT); });
      if (this.stage) {
        /* the mouse on the stage: this colour's other frames start; resting on the photograph itself (not an
           arrow: the arrows sit outside .magwrap) for SHARP_MS, the full-size frame for the magnifier and the viewer */
        on(this.stage, 'pointerover', function (e) {
          if (e.pointerType !== 'mouse') return;
          self.ahead(false);
          var t = e.target;
          if (t && t.closest && t.closest('.magwrap') && !self.SHARPT) self.SHARPT = setTimeout(function () { self.SHARPT = null; self.sharpen(); }, SHARP_MS);
        });
        on(this.stage, 'pointerout', function (e) {
          var r = e.relatedTarget;
          if (!(r && r.closest && r.closest('.magwrap') && self.stage.contains(r))) { clearTimeout(self.SHARPT); self.SHARPT = null; }
        });
        /* the stage in view: fetch the next frame, so the first arrow click is instant too (not while the film
           starts on its own: the shopper picks a colour first) */
        if ('IntersectionObserver' in window && !(this.fam.film && this.fam.filmauto)) {
          this.io2 = new IntersectionObserver(function (es) {
            es.forEach(function (e) { if (e.isIntersecting && self.io2) { self.io2.disconnect(); self.io2 = null; self.ahead(true); } });
          }, { threshold: 0.4 });
          this.io2.observe(this.stage);
        }
        /* a wider window (or the browser zoomed in): the frame on screen is fetched again at the size it now
           needs, and the frames ahead are sized again when next asked for (AHEADKEY forgotten) */
        on(window, 'resize', function () { clearTimeout(self.RSZ); self.RSZ = setTimeout(function () { self.AHEADKEY = null; self.resharp(); }, 250); });
      }
      this.render(null);
      this.warm();
      this.autoFilm();
    }
    disconnectedCallback() {
      if (this._unbind) { this._unbind(); this._unbind = null; }
      this.clearHover();
      clearTimeout(this.NAMETIMER); (this._timers || []).forEach(clearTimeout); this._timers = [];
      if (this.io) { this.io.disconnect(); this.io = null; }
      if (this.io2) { this.io2.disconnect(); this.io2 = null; }
      clearTimeout(this.PQT); clearTimeout(this.RSZ); clearTimeout(this.CHIPT); clearTimeout(this.SHARPT); this.SHARPT = null; this.PQ = null; this.PQBUSY = false; this.KEPT = [];
      if (this.WIPEJOB) this.cancelWipe(false);
    }
    clearHover() { if (this.hoverT) { clearTimeout(this.hoverT); this.hoverT = null; } }

    /* ---- selection model ---- */
    cur() { var f = this.fam, st = this.st, col = this.colors[st.c]; return { f: f, st: st, color: col, size: f.sizes[st.s] || f.sizes[0], addon: f.addons ? f.addons[st.a] : null }; }
    views() { var c = this.cur(), pics = c.color.pics; if (this.fam.photos_only && pics.length) return pics.slice(); return pics.concat(c.color.img ? [c.color.img] : []); }
    lpViews() { var v = this.views(); return { list: v, i: Math.min(this.st.v, Math.max(0, v.length - 1)) }; }
    /* a size or insert choice names a real variant: by title (large / pocket / lined / dotted / blank),
       else the variant named after the colourway (the inserts), else the first that can be bought */
    variantFor(col, size, addon) {
      var vs = col.variants || []; if (!vs.length) return null;
      var want = addon ? String(addon.l).replace(/^\+\s*/, '') : (size && this.fam.sizes.length > 1 ? size.id : null);
      if (want) { var w = want.toLowerCase(); var hit = vs.filter(function (v) { return v.title.toLowerCase().indexOf(w) > -1; })[0]; return hit || null; }
      var byName = vs.filter(function (v) { return String(col.n).length > 1 && v.title.toLowerCase().indexOf(String(col.n).toLowerCase()) > -1; })[0];
      if (byName) return byName;
      return vs.filter(function (v) { return v.available; })[0] || vs[0];
    }
    sizeDisabled(col, size) { return this.fam.sizes.length > 1 && !this.variantFor(col, size, null); }
    soldOut(c) { var v = this.variantFor(c.color, c.size, c.addon); return !!(c.color.oos || (c.color.variants.length && (!v || !v.available))); }
    unitPrice(c) { var v = this.variantFor(c.color, c.size, c.addon); if (v) return v.price; return Math.round(((c.size && c.size.p) || 0) * 100 + ((c.addon && c.addon.p) || 0) * 100); }

    /* ---- the wipe job ---- */
    cancelWipe(commit) {
      var j = this.WIPEJOB; if (!j) return;
      clearTimeout(j.timer);
      (j.ghosts || []).forEach(function (g) { if (g && g.parentNode) g.parentNode.removeChild(g); });
      /* ORDER IS THE FIX: commit paints the final colours UNDERNEATH the still-held effect, then the effect is lifted */
      if (commit && j.commit) j.commit();
      (j.anims || []).forEach(function (a) { try { a.cancel(); } catch (e) {} });
      if (j.img) {
        j.img.classList.remove('wipein', 'go');
        ['--ox', '--oy', '--r', 'clip-path', 'z-index', 'position'].forEach(function (p) { j.img.style.removeProperty(p); });
      }
      this.WIPEJOB = null;
    }
    liveColor(hex) {
      if (!this.fam.live || !hex) return;
      var scope = this.closest('main') || document.body;
      scope.classList.add('livecol');
      scope.style.setProperty('--live', hex);
      scope.style.setProperty('--live-deep', shade(hex, 0.72));
    }
    warm() {
      if (this.WARMED || !this.fam.live || phoneLayout()) return; this.WARMED = 1;
      var self = this, cols = this.colors;
      /* at the stage's own size (sized): the address the wipe will ask for, and a sixth of the bytes on a 1x screen */
      var go = function () { cols.forEach(function (col) { (col.pics || []).slice(0, 1).forEach(function (src) { var u = self.sized(src); if (self.GOT[u]) return; self.GOT[u] = 1; var im = new Image(); im.src = u; self.keep(im); if (im.decode) im.decode().catch(function () {}); }); }); };
      if (window.requestIdleCallback) window.requestIdleCallback(go, { timeout: 2000 }); else this._timers.push(setTimeout(go, 400));
    }
    /* the address the stage shows for a frame: the smallest STEPS width that covers the stage's width x the
       screen's pixel density, never above the width the section gave (1600). An address with no width (not
       Shopify's CDN) and a hidden stage (phones) keep the address as given. */
    sized(u) {
      var full = widthOf(u); if (!full || !this.stage) return u;
      var need = Math.ceil((this.stage.clientWidth || 0) * (window.devicePixelRatio || 1));
      if (!need) return u;
      for (var k = 0; k < STEPS.length; k++) if (STEPS[k] >= need) return STEPS[k] >= full ? u : atWidth(u, STEPS[k]);
      return u;
    }
    /* one frame fetched ahead; the browser keeps it (Shopify's CDN allows a year) and the stage's <img> finds it */
    grab(u, hi, cb) {
      if (!u || this.GOT[u]) { if (cb) cb(); return; }
      this.GOT[u] = 1;
      var im = new Image();
      try { im.fetchPriority = hi ? 'high' : 'low'; } catch (e) {}
      im.onload = im.onerror = function () { im.onload = im.onerror = null; if (cb) cb(); };
      im.src = u;
      this.keep(im);
    }
    keep(im) { var k = this.KEPT || (this.KEPT = []); k.push(im); if (k.length > KEEP_N) k.shift(); }
    /* this colour's other frames, nearest first: next, previous, then onward. only = just the next one. One at
       a time, so the frame wanted soonest never shares the connection with the rest. The queue keeps the
       section's addresses and pump() sizes each one as it goes, so a resize part-way is followed. */
    ahead(only) {
      if (!this.stage || !this.stage.clientWidth) return;
      var views = this.views(), n = views.length; if (n < 2) return;
      var i = Math.min(this.st.v, n - 1), key = this.st.c + ':' + i + (only ? ':1' : '');
      if (this.AHEADKEY === key) return; this.AHEADKEY = key;
      var q = [], add = function (j) { var u = views[((j % n) + n) % n]; if (u && q.indexOf(u) < 0) q.push(u); };
      add(i + 1);
      if (!only) { add(i - 1); for (var k = 2; k < n - 1; k++) add(i + k); }
      this.PQ = q; this.PQHI = true;
      if (!this.PQBUSY) this.pump();
    }
    pump() {
      var self = this; this.PQBUSY = false;
      if (!this.isConnected || !this.PQ) return;
      /* the stage hidden (the window narrowed to the phone layout): nothing on screen to fetch for */
      if (!this.stage || !this.stage.clientWidth) { this.PQ = null; this.AHEADKEY = null; return; }
      /* while the photo viewer is open it fetches its own full-size frames; the stage's wait until it closes */
      var lb = document.getElementById('lbox');
      if (lb && lb.classList.contains('open')) { this.PQBUSY = true; clearTimeout(this.PQT); this.PQT = setTimeout(function () { self.pump(); }, 800); return; }
      var u = null; while (!u && this.PQ.length) { u = this.sized(this.PQ.shift()); if (this.GOT[u]) u = null; }
      if (!u) return;
      var hi = this.PQHI; this.PQHI = false; this.PQBUSY = true;
      this.grab(u, hi, function () { self.pump(); });
    }
    /* the magnifier (2.5x) and the viewer want the full frame, not the stage-sized one. Not while the frame on
       screen is still arriving or a colour is wiping in: the larger copy would share their connection (or be
       refused by swapTo); it is tried again once they are done, if the mouse is still on the photograph. */
    sharpen() {
      var self = this, img = this.stage && this.stage.querySelector('img.zoomable'); if (!img) return;
      var again = function (ms) {
        clearTimeout(self.SHARPT);
        self.SHARPT = setTimeout(function () { self.SHARPT = null; if (img.isConnected && img.parentNode && img.parentNode.matches(':hover')) self.sharpen(); }, ms);
      };
      if (this.WIPEJOB) { again(400); return; }
      if (!img.complete) { ready(img, HOLD_MS).then(function () { if (self.SHARPT === null) again(120); }); return; }
      this.swapTo(img, this.views()[this.st.v]);
    }
    /* never for a hidden stage (sized() gives the full address there) */
    resharp() { var img = this.stage && this.stage.clientWidth ? this.stage.querySelector('img.zoomable') : null; if (img) this.swapTo(img, this.sized(this.views()[this.st.v] || '')); }
    /* the same photograph, larger: fetched and decoded first, then the address changes, so nothing flickers */
    swapTo(img, u) {
      var self = this, cur = img.getAttribute('src');
      if (!u || !samePhoto(cur, u) || widthOf(u) <= widthOf(cur) || this.SWAPTO === u || this.WIPEJOB) return;
      this.SWAPTO = u; this.GOT[u] = 1;
      var im = new Image();
      try { im.fetchPriority = 'high'; } catch (e) {}
      var go = function () {
        if (self.SWAPTO === u) self.SWAPTO = null;
        if (!img.isConnected || img.getAttribute('src') !== cur || self.WIPEJOB) return;
        img.setAttribute('src', u);
        /* a magnifier already open was painted from the smaller file: it takes the full one now, not at the next
           mouse move (lp-zoom.js paints the lens from the image's own file) */
        var lens = img.parentNode && img.parentNode.querySelector('.maglens');
        if (lens && lens.style.backgroundImage) lens.style.backgroundImage = 'url("' + u + '")';
      };
      im.onload = function () { if (im.decode) im.decode().then(go, go); else go(); };
      im.onerror = function () { if (self.SWAPTO === u) self.SWAPTO = null; };
      im.src = u;
    }

    /* ---- render ---- */
    render(FROM) {
      var self = this, f = this.fam, st = this.st, c = this.cur();
      FROM = (f.live && FROM && isFinite(FROM.vx) && isFinite(FROM.vy)) ? { vx: FROM.vx, vy: FROM.vy } : null;
      var staleAnims = null;
      var DEFER = [];
      var views = this.views();
      if (st.v >= views.length) st.v = 0;
      var hasRend = !(f.photos_only && c.color.pics.length);
      var isRender = hasRend && views.length && (st.v === views.length - 1) && !!c.color.img;
      var stage = this.stage;
      if (stage) {
        stage.className = 'bstage' + (isRender ? '' : ' photo');
        var prev = stage.querySelector('img.zoomable');
        var prevSrc = prev ? prev.getAttribute('src') : null;
        var src = this.sized(views[st.v] || '');   /* the stage's size of the frame (30 Sep 2026, see STEPS) */
        var moved = false;
        var magHTML = '<img class="zoomable"' + (phoneLayout() ? ' loading="lazy"' : '') + ' src="' + esc(src) + '" alt="' + esc(c.color.n) + '"><div class="maglens"></div><span class="maghint">hover to zoom · click to enlarge</span>';
        if (prev && prevSrc && !samePhoto(prevSrc, src)) {
          moved = true;
          var mag = stage.querySelector('.magwrap');
          /* hold the frame on screen, point the live image at the new file underneath, then retire the held copy */
          var ghost = prev.cloneNode(false); ghost.removeAttribute('id');
          ['position', 'z-index', 'clip-path'].forEach(function (p) { ghost.style.removeProperty(p); });
          if (FROM) {
            /* superseding a wipe mid-flight: freeze the in-flight circle where it is and wipe over the whole sandwich */
            var adopted = [], frozen = null, old = this.WIPEJOB;
            if (old) {
              clearTimeout(old.timer); adopted = old.ghosts || [];
              if (old.img) { frozen = getComputedStyle(old.img).clipPath; old.img.classList.remove('wipein', 'go'); ['--ox', '--oy', '--r'].forEach(function (p) { old.img.style.removeProperty(p); }); }
              staleAnims = (old.anims || []).slice(); this.WIPEJOB = null;
            }
            ghost.className = 'wipeunder';
            if (frozen && frozen !== 'none') ghost.style.clipPath = frozen;
            mag.insertBefore(ghost, prev);
            prev.setAttribute('src', src); prev.setAttribute('alt', c.color.n);
            var so = originIn(stage, FROM) || { x: 50, y: 50, r: Math.ceil(Math.hypot(stage.offsetWidth || 900, stage.offsetHeight || 700)) };
            prev.style.position = 'relative'; prev.style.zIndex = '1';
            var CLIP0 = 'circle(0px at ' + so.x + '% ' + so.y + '%)', CLIP1 = 'circle(' + so.r + 'px at ' + so.x + '% ' + so.y + '%)';
            prev.style.clipPath = CLIP0;
            var job = this.WIPEJOB = { ghosts: adopted.concat([ghost]), img: prev, timer: null, anims: [] };
            var fin = function () { if (self.WIPEJOB !== job) return; self.cancelWipe(true); };
            var arm = function () {
              if (self.WIPEJOB !== job) return;
              if (noMotion() || !prev.animate) { fin(); return; }
              var a = prev.animate([{ clipPath: CLIP0 }, { clipPath: CLIP1 }], { duration: wipeDur(), easing: 'cubic-bezier(.42,.06,.26,1)', fill: 'forwards' });
              job.anims.push(a);
              if (a.finished && a.finished.then) a.finished.then(fin, function () {});
              job.timer = setTimeout(fin, wipeMs() + 250);
            };
            /* the reveal must not outrun the pixels: wait for the decode, capped at 300ms */
            if (prev.decode) { var armed = false; var once = function () { if (!armed) { armed = true; arm(); } }; prev.decode().then(once, once); setTimeout(once, 300); } else arm();
          } else {
            this.cancelWipe(false);
            /* The original fade, unchanged in timing: it starts at once and runs .34s. A version that held the old
               frame until the next one decoded, then sometimes faded faster, was tried on 30 Sep 2026 and reverted
               the same day (Luke: "I just want the Inspire page reverted a little"); the frames are now fetched
               ahead (ahead/pump), so the next one is normally already here when the fade starts. */
            ghost.className = 'fadeout';
            prev.setAttribute('src', src); prev.setAttribute('alt', c.color.n);
            mag.appendChild(ghost);
            requestAnimationFrame(function () { requestAnimationFrame(function () { ghost.classList.add('go'); setTimeout(function () { if (ghost.parentNode) ghost.parentNode.removeChild(ghost); }, 380); }); });
          }
        } else if (prev) {
          /* the same photograph (perhaps already swapped for its full-size copy by sharpen): leave the file alone */
          if (!samePhoto(prevSrc, src)) prev.setAttribute('src', src);
          prev.setAttribute('alt', c.color.n);
        } else {
          moved = !!FROM;   /* coming off the film onto a chosen colour */
          /* no previous still — the first paint, or the film was playing: build the picture, then open it from the swatch */
          this.cancelWipe(false);
          var film = (FILMHANDOFF && stage.querySelector('.filmwrap')) || null;
          if (film) {
            var fvid = film.querySelector('video'); var wasPlaying = !!(fvid && !fvid.paused);
            var magNew = document.createElement('div'); magNew.className = 'magwrap overfilm'; magNew.innerHTML = magHTML;
            stage.appendChild(magNew);
            magNew.insertBefore(film, magNew.firstChild);   /* the film goes INSIDE the photograph's box */
            film.classList.add('filmheld');
            if (wasPlaying && fvid && fvid.paused) { var pr = fvid.play(); if (pr && pr.catch) pr.catch(function () {}); }
          } else {
            stage.innerHTML = '<div class="magwrap">' + magHTML + '</div>';
          }
          if (FROM) {
            var img = stage.querySelector('img.zoomable');
            var fo = originIn(stage, FROM) || { x: 50, y: 50, r: 1400 };
            var FCLIP0 = 'circle(0px at ' + fo.x + '% ' + fo.y + '%)', FCLIP1 = 'circle(' + fo.r + 'px at ' + fo.x + '% ' + fo.y + '%)';
            img.style.position = 'relative'; img.style.zIndex = '1'; img.style.clipPath = FCLIP0;
            var job2 = this.WIPEJOB = { ghosts: film ? [film] : [], img: img, timer: null, anims: [] };
            var fin2 = function () { if (self.WIPEJOB !== job2) return; self.cancelWipe(true); };
            var arm2 = function () {
              if (self.WIPEJOB !== job2) return;
              if (noMotion() || !img.animate) { fin2(); return; }
              var a = img.animate([{ clipPath: FCLIP0 }, { clipPath: FCLIP1 }], { duration: wipeDur(), easing: 'cubic-bezier(.42,.06,.26,1)', fill: 'forwards' });
              job2.anims.push(a);
              if (a.finished && a.finished.then) a.finished.then(fin2, function () {});
              job2.timer = setTimeout(fin2, wipeMs() + 250);
            };
            if (img.decode) { var armed2 = false; var once2 = function () { if (!armed2) { armed2 = true; arm2(); } }; img.decode().then(once2, once2); setTimeout(once2, 300); } else arm2();
          } else if (film) {
            if (film.parentNode) film.parentNode.removeChild(film);   /* no origin, no animation: the old hard cut is right here */
          }
        }
        if (src) this.GOT[src] = 1;
        /* a new frame on screen: once it is ready, the rest of this colour follows, nearest first. A later click
           that moved on before it was ready starts the queue itself (a browser rejects decode() when the file
           changes, which would otherwise start the queue while the new frame is still arriving) */
        if (moved) {
          var now = stage.querySelector('img.zoomable'), shown = src;
          if (now) ready(now, HOLD_MS).then(function () { if (samePhoto(now.getAttribute('src'), shown)) self.ahead(false); });
        }
        /* view arrows over the photograph */
        var many = views.length > 1, nav = stage.querySelector('.gnavwrap');
        if (many && !nav) {
          nav = document.createElement('div'); nav.className = 'gnavwrap';
          nav.innerHTML = '<button class="gnav gprev" type="button" aria-label="previous view" data-step="-1">&#8249;</button><button class="gnav gnext" type="button" aria-label="next view" data-step="1">&#8250;</button>';
          stage.appendChild(nav);
        }
        if (nav) nav.style.display = many ? '' : 'none';
      }
      /* the band and the buttons wait for the wipe; the chips do not */
      if (FROM) DEFER.push(function () { self.liveColor(c.color.hex); }); else this.liveColor(c.color.hex);
      /* the colour name runs ahead of the rest — halfway through the wipe */
      var nm = this.querySelector('[data-cname]');
      if (nm) {
        var seq = (this.NAMESEQ = (this.NAMESEQ || 0) + 1);
        var setName = function () { if (self.NAMESEQ !== seq) return; nm.textContent = c.color.n; };
        clearTimeout(this.NAMETIMER);
        if (FROM) { this.NAMETIMER = setTimeout(setName, Math.round(wipeDur() * CNAMEAT)); DEFER.push(setName); } else setName();
      }
      var priceEl = this.querySelector('[data-price]'); if (priceEl) priceEl.textContent = money(this.unitPrice(c));
      /* the star rating follows the swatch: every colourway's rating is rendered by the section and all
         but the selected one are hidden, so there is no second star implementation here */
      var starWrap = this.querySelector('[data-stars]');
      if (starWrap) {
        var want = String(c.color.id);
        Array.prototype.forEach.call(starWrap.children, function (el) {
          el.classList.toggle('on', el.getAttribute('data-star-for') === want);
        });
      }
      /* sizes */
      var sizesEl = this.querySelector('[data-sizes]');
      if (sizesEl) {
        /* 19 Oct 2026 (Luke: "remove the pocket option from the heritage classic colors that don't have them because
           currently it just becomes grayed out but we should try and remove them completely"): a size this colourway has
           no variant for is left out, not greyed. A size it does make but has sold out keeps its chip (sizeDisabled is
           false for it), so the buy zone still says sold out. data-pick-s stays the family index, so pick() is unchanged. */
        sizesEl.innerHTML = f.sizes.map(function (s, i) {
          /* an invisible stand-in keeps the row's height, so the buttons below don't jump when a colour has fewer sizes */
          if (self.sizeDisabled(c.color, s)) return '<button type="button" class="schip schip--ghost" disabled tabindex="-1" aria-hidden="true">' + esc(s.l) + '</button>';
          return '<button type="button" class="schip' + (i === st.s ? ' on' : '') + '" data-pick-s="' + i + '">' + esc(s.l) + '</button>';
        }).join('');
        sizesEl.parentElement.style.display = (f.sizes.length === 1 && !f.sizes[0].l) ? 'none' : '';
      }
      /* colour chips */
      var cwrap = this.querySelector('[data-colors]');
      if (cwrap) {
        var ch = this.colors.map(function (col, i) {
          /* a colourway with no swatch colour — the three paper refills, whose box photographs are all but
             identical — shows its name in a boxed chip like the sizes. Luke, 24 Sep 2026: "just write out
             'Lined' and put a box around it … so that its just easier to select". Sold out reads struck through. */
          if (!col.hex) return '<button type="button" class="cchip ctext' + (i === st.c ? ' on' : '') + (col.oos ? ' oos' : '') + '" data-ci="' + i + '" data-pick-c="' + i + '" title="' + esc(col.n + (col.oos ? ' — sold out' : '')) + '" aria-label="' + esc(col.n + (col.oos ? ' — sold out' : '')) + '">' + esc(col.n) + '</button>';
          var inner = col.hex
            ? '<span class="cfill" style="background:' + esc(col.hex) + '">' + (col.hex2 ? '<span class="cstripe" style="background:' + esc(col.hex2) + '"></span>' : '') + '</span>'
            : (col.chip ? '<img src="' + esc(col.chip) + '" alt="' + esc(col.n) + '" width="52" height="52">' : '<span class="cfill" style="background:var(--tile)"></span>');
          return '<button type="button" class="cchip' + (i === st.c ? ' on' : '') + (col.oos ? ' oos' : '') + '" data-ci="' + i + '" data-pick-c="' + i + '" title="' + esc(col.n + (col.oos ? ' — sold out' : '')) + '" aria-label="' + esc(col.n + (col.oos ? ' — sold out' : '')) + '">' + inner + '</button>';
        }).join('');
        if (f.film) {
          var filmchip = '<button type="button" class="cchip filmchip' + (stage && stage.classList.contains('filmon') ? ' playing' : '') + '" title="watch the film" aria-label="watch the film" data-film><span class="cfill"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M9.8 8 L16.4 12 L9.8 16 Z"/></svg></span></button>';
          ch = f.filmlast ? ch + filmchip : filmchip + ch;
        }
        cwrap.innerHTML = ch;
        cwrap.classList.toggle('swatchbar', f.swatchbar === true);
        var onchip = cwrap.querySelector('.cchip.on');
        if (onchip && cwrap.scrollWidth > cwrap.clientWidth + 4) {
          var target = onchip.offsetLeft - (cwrap.clientWidth - onchip.offsetWidth) / 2;
          cwrap.scrollLeft = Math.max(0, Math.min(target, cwrap.scrollWidth - cwrap.clientWidth));
        }
      }
      /* the collection word in the heading takes the selected colour */
      var word = this.querySelector('[data-name] .bnamec'); if (word) word.style.color = c.color.hex || '';
      /* inserts */
      var aw = this.querySelector('[data-addons]');
      if (aw && f.addons) aw.innerHTML = f.addons.map(function (a, i) { return '<button type="button" class="schip' + (i === st.a ? ' on' : '') + '" data-pick-a="' + i + '">' + esc(a.l) + '</button>'; }).join('');
      /* buy zone */
      var bz = this.querySelector('[data-buyzone]');
      if (bz) {
        /* details opens the product page on the size or insert chosen here, so the two pages agree */
        var dv = this.variantFor(c.color, c.size, c.addon);
        var details = '<a class="dbtn" href="' + esc((c.color.url || '#') + (dv && c.color.url ? '?variant=' + dv.id : '')) + '"' ;
        if (this.soldOut(c)) {
          var j = -1; this.colors.some(function (cc, idx) { if (idx !== st.c && !self.soldOut({ color: cc, size: c.size, addon: c.addon })) { j = idx; return true; } return false; });
          bz.innerHTML = '<div class="oosline">sold out</div>'
            + (f.notify ? '<div class="notify"><a class="dlink" href="mailto:' + esc(f.notify) + '?subject=' + encodeURIComponent('Tell me when ' + c.color.title + ' returns') + '">email us when it returns</a></div>' : '')
            + (j > -1 ? '<p class="oosalt">in stock now: <button type="button" class="dlink" style="font-size:11px;" data-pick-c="' + j + '">' + esc(this.colors[j].n) + ' ></button></p>' : '')
            + '<div class="bstory oosstory">' + details + '>' + esc(f.details_label || 'details') + '</a></div>';
        } else {
          var tint = (f.tintbuy && c.color.hex) ? c.color.hex : null;
          var from = tint ? (this.BTNPREV || tint) : null;
          var tintstyle = from ? ' style="background:' + from + ';border-color:' + from + ';color:' + readableOn(from) + '"' : '';
          bz.innerHTML = '<div class="qtyrow"><button type="button" class="atc"' + tintstyle + ' data-atc>add to bag</button></div>'
            + '<div class="bstory"><div class="qty"><button type="button" data-qty="-1" aria-label="fewer">&minus;</button><span>' + st.q + '</span><button type="button" data-qty="1" aria-label="more">+</button></div>'
            + details + tintstyle + '>' + esc(f.details_label || 'details') + '</a></div>';
          if (tint && from !== tint) {
            var atcB = bz.querySelector('.atc'), dbtnB = bz.querySelector('.dbtn');
            requestAnimationFrame(function () { requestAnimationFrame(function () { [atcB, dbtnB].forEach(function (btn) { if (!btn) return; btn.style.background = tint; btn.style.borderColor = tint; btn.style.color = readableOn(tint); }); }); });
          }
          if (tint) this.BTNPREV = tint;
        }
      }
      if (DEFER.length) { var jb = this.WIPEJOB; if (jb) jb.commit = function () { DEFER.forEach(function (fn) { fn(); }); }; else DEFER.forEach(function (fn) { fn(); }); }
      if (staleAnims) staleAnims.forEach(function (a) { try { a.cancel(); } catch (e) {} });
    }

    /* ---- interaction ---- */
    onClick(e) {
      var t = e.target; if (!t || !t.closest) return;
      var el;
      if ((el = t.closest('[data-pick-c]')) && this.contains(el)) { this.clearHover(); this.pick('c', parseInt(el.getAttribute('data-pick-c'), 10), el.classList.contains('cchip') ? el : null); return; }
      if ((el = t.closest('[data-pick-s]')) && this.contains(el)) { if (!el.disabled) this.pick('s', parseInt(el.getAttribute('data-pick-s'), 10)); return; }
      if ((el = t.closest('[data-pick-a]')) && this.contains(el)) { this.pick('a', parseInt(el.getAttribute('data-pick-a'), 10)); return; }
      if ((el = t.closest('[data-qty]')) && this.contains(el)) { this.st.q = Math.max(1, this.st.q + parseInt(el.getAttribute('data-qty'), 10)); this.render(null); return; }
      if ((el = t.closest('[data-step]')) && this.contains(el)) { this.stepView(parseInt(el.getAttribute('data-step'), 10)); return; }
      if ((el = t.closest('[data-atc]')) && this.contains(el)) { this.addBag(); return; }
      if ((el = t.closest('[data-film]')) && this.contains(el)) { this.clearHover(); this.playFilm(); return; }
      if ((el = t.closest('video.stagefilm')) && this.contains(el)) { if (el.paused) { var p = el.play(); if (p && p.catch) p.catch(function () {}); } else el.pause(); }
    }
    pick(dim, i, chip) {
      var st = this.st, f = this.fam;
      if (dim === 'c' && (i < 0 || i >= this.colors.length)) return;
      var onfilm = FILMHANDOFF && !!(this.stage && this.stage.querySelector('.filmwrap'));
      var FROM = null;
      if (dim === 'c' && f.live && (st.c !== i || onfilm)) {
        chip = chip || this.querySelector('.cchip[data-ci="' + i + '"]');
        if (chip) { var cr = chip.getBoundingClientRect(); FROM = { vx: cr.left + cr.width / 2, vy: cr.top + cr.height / 2 }; }
      }
      if (dim === 'c') {
        st.c = i; st.v = 0;
        var sz = f.sizes[st.s], self2 = this;
        /* 19 Oct 2026: fall back to the first size this colour is made in (not blindly to size 0) */
        if (sz && this.sizeDisabled(this.colors[i], sz)) { var k = f.sizes.findIndex(function (s2) { return !self2.sizeDisabled(self2.colors[i], s2); }); st.s = k < 0 ? 0 : k; }
      }
      if (dim === 's') { if (this.sizeDisabled(this.colors[st.c], f.sizes[i])) return; st.s = i; }
      if (dim === 'a') { st.a = i; }
      this.render(FROM);
    }
    stepView(d) { var views = this.views(); if (views.length < 2) return; this.st.v = (this.st.v + d + views.length) % views.length; this.render(null); }
    playFilm() {
      var f = this.fam, stage = this.stage; if (!f.film || !stage) return;
      this.cancelWipe(false);
      stage.className = 'bstage photo filmon';
      stage.innerHTML = '<div class="filmwrap"><video class="stagefilm" src="' + esc(f.film) + '"' + (f.poster ? ' poster="' + esc(f.poster) + '"' : '') + ' autoplay muted loop playsinline></video></div>';
      var cwrap = this.querySelector('[data-colors]');
      if (cwrap) { var on = cwrap.querySelector('.cchip.on'); if (on) on.classList.remove('on'); var fc = cwrap.querySelector('.filmchip'); if (fc) fc.classList.add('playing'); }
    }
    /* autoplay once per visit: the first time the stage scrolls into view the film starts, muted */
    autoFilm() {
      var self = this; if (!this.fam.film || !this.fam.filmauto || !('IntersectionObserver' in window) || !this.stage) return;
      var scroller = document.querySelector('.page-wrapper');
      this.io = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          var scrolled = (scroller && scroller.scrollTop > 40) || window.scrollY > 40;
          if (e.isIntersecting && scrolled && !self.FILMAUTO) { self.FILMAUTO = true; self.playFilm(); self.io.disconnect(); }
        });
      }, { threshold: 0.4 });
      this.io.observe(this.stage);
    }
    addBag() {
      var c = this.cur(); if (this.soldOut(c)) return;
      var v = this.variantFor(c.color, c.size, c.addon); if (!v) return;
      var q = this.st.q, hex = c.color.hex || null, self = this;
      this.st.q = 1; this.render(null); this.atcPop(hex);
      cartAdd(this, v.id, q, c.color.product_id).catch(function (err) { if (window.console) console.error('lp-shelf: add to bag failed', err); });
    }
    atcPop(hex) {
      var btn = this.querySelector('[data-buyzone] .atc'); if (!btn) return;
      var palette = this.colors.map(function (c) { return c.hex; }).filter(Boolean);
      if (!palette.length) palette = ['#a8875a', '#ecd9ac', '#6e2b26'];
      btn.classList.remove('pop'); void btn.offsetWidth; btn.classList.add('pop');
      this._timers.push(setTimeout(function () { btn.classList.remove('pop'); }, 700));
      this.confetti(btn, palette);
    }
    /* one confetti implementation, in lp-cart.js, which both the shelf and the product page load */
    confetti(btn, palette) { if (window.lpConfetti) window.lpConfetti(btn, palette); }
  }
  customElements.define('lp-shelf', LPShelf);

  /* jump to the shelf the address names (/collections/heritage-collection#shelf-refill). The retired
     sub-collections forward here and the landing pages' photographs link here (Luke, 24 Sep 2026: "taking
     straight to the shelf"). The browser's own jump misses: at 990px and up Horizon scrolls .page-wrapper, not
     the document, and the shelves fill in after the first paint. So it is done here, just below the sticky
     header, once the shelves have rendered and again after load in case photographs above moved the shelf —
     unless the visitor has scrolled in the meantime. */
  var jumped = null;
  function shelfTarget() { var h = ''; try { h = decodeURIComponent(location.hash || '').slice(1); } catch (e) {} return /^shelf-[a-z0-9-]+$/.test(h) ? document.getElementById(h) : null; }
  function scroller() { return (window.matchMedia && matchMedia('(min-width: 990px)').matches && document.querySelector('.page-wrapper')) || document.scrollingElement || document.documentElement; }
  function jumpToShelf(again) {
    var el = shelfTarget(); if (!el) return;
    var sc = scroller();
    if (again && jumped !== null && Math.abs(sc.scrollTop - jumped) > 4) return;   /* the visitor moved; leave them */
    var hdr = document.querySelector('#header-component');
    var off = (hdr ? hdr.getBoundingClientRect().height : 60) + 24;
    var base = sc === document.scrollingElement || sc === document.documentElement ? 0 : sc.getBoundingClientRect().top;
    var top = Math.max(0, Math.round(el.getBoundingClientRect().top - base + sc.scrollTop - off));
    sc.scrollTo({ top: top, behavior: 'instant' });
    jumped = sc.scrollTop;
  }
  function soon() { requestAnimationFrame(function () { requestAnimationFrame(function () { jumpToShelf(false); }); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', soon); else soon();
  window.addEventListener('load', function () { setTimeout(function () { jumpToShelf(true); }, 120); });
  window.addEventListener('hashchange', function () { jumped = null; jumpToShelf(false); });
})();
